// client/src/pages/Admin/ResourceManager/components/ResourceCard.tsx
import React, { type FC, type MouseEvent } from 'react';
import {
  Pencil,
  Trash2,
  MoreVertical,
  Star,
  User,
  Calendar,
  Download,
  Eye
} from 'lucide-react';
import { ResourceStatus } from '../../../../shared/models/Resource';
import type { Resource } from '../../../../shared/models/Resource';
import { ResourceStatusBadge } from './ResourceStatusBadge';
import {
  getParentCategoryDisplay,
  getCategoryDisplay,
  truncate,
  formatDate,
  formatStatusLabel
} from '../utils/resourceHelpers';
import { highlightMatch } from '../utils/resourceSearch';

interface ResourceCardProps {
  resource: Resource;
  isSelected: boolean;
  onToggleSelection: (id: string) => void;
  onEdit: (resource: Resource) => void;
  onDelete: (id: string) => void;
  onStatusChange: (id: string, status: string) => void;
  onToggleActionMenu: (id: string, e: MouseEvent) => void;
  isActionMenuOpen: boolean;
  matchFields: string[];
  debouncedSearchTerm: string;
}

export const ResourceCard: FC<ResourceCardProps> = ({
  resource,
  isSelected,
  onToggleSelection,
  onEdit,
  onDelete,
  onStatusChange,
  onToggleActionMenu,
  isActionMenuOpen,
  matchFields,
  debouncedSearchTerm
}) => {
  const parentLabel = getParentCategoryDisplay(resource.category);
  const categoryLabel = getCategoryDisplay(resource.category);
  
  // ✅ FIX: Safe array check for matchFields
  const safeMatchFields = Array.isArray(matchFields) ? matchFields : [];
  const hasMatches = safeMatchFields.length > 0;

  const highlightedTitle = hasMatches && safeMatchFields.includes('title')
    ? highlightMatch(resource.title || '', debouncedSearchTerm)
    : resource.title || 'Untitled';

  const highlightedDescription = hasMatches && safeMatchFields.includes('description')
    ? highlightMatch(resource.description || '', debouncedSearchTerm)
    : truncate(resource.description || '', 140);

  const statusOptions: ResourceStatus[] = [
    ResourceStatus.DRAFT,
    ResourceStatus.PUBLISHED,
    ResourceStatus.UNDER_REVIEW,
    ResourceStatus.ARCHIVED
  ];

  // ✅ FIX: Safe array check for tags
  const safeTags = Array.isArray(resource.tags) ? resource.tags : [];
  const safeAuthors = Array.isArray(resource.authors) ? resource.authors : [];

  return (
    <div className={`resource-card ${isSelected ? 'selected' : ''}`}>
      {resource.isFeatured && (
        <div className="featured-ribbon">
          <Star size={12} />
          Featured
        </div>
      )}

      <div className="resource-card-inner">
        <div className="resource-card-checkbox">
          <input
            type="checkbox"
            className="resource-checkbox"
            checked={isSelected}
            onChange={() => onToggleSelection(resource._id!)}
            aria-label={`Select ${resource.title || 'resource'}`}
          />
        </div>

        <div className="resource-card-content">
          <div className="resource-card-header">
            <div className="resource-card-title-section">
              <h3 
                className="resource-card-title"
                dangerouslySetInnerHTML={{ __html: highlightedTitle }}
              />
              <div className="resource-card-badges">
                {resource.isFeatured && (
                  <span className="featured-badge">
                    <Star size={12} />
                  </span>
                )}
              </div>
            </div>
            <div className="resource-card-status">
              <ResourceStatusBadge status={resource.status} />
            </div>
          </div>

          <div className="resource-card-categories">
            {parentLabel && (
              <span className="parent-category">{parentLabel}</span>
            )}
            <span className="category-label">{categoryLabel}</span>
          </div>

          <div className="resource-card-description-wrapper">
            <p 
              className="resource-card-description"
              dangerouslySetInnerHTML={{ __html: highlightedDescription }}
            />
          </div>

          {hasMatches && safeMatchFields.length > 0 && (
            <div className="match-indicators">
              <span className="match-label">Matches in:</span>
              {safeMatchFields.map((field, index) => {
                // ✅ FIX: Safely get the label for each field
                let label = '';
                if (field === 'title') label = 'Title';
                else if (field === 'description') label = 'Description';
                else if (field === 'tags') label = 'Tags';
                else if (field === 'authors') label = 'Authors';
                else if (field === 'category') label = 'Category';
                else if (field === 'parentCategory') label = 'Parent Category';
                else if (field === 'files') label = 'File Names';
                else label = field; // fallback to the field name itself
                
                return (
                  <span key={index} className="match-tag">
                    {label}
                  </span>
                );
              })}
            </div>
          )}

          <div className="resource-card-meta">
            <div className="meta-left">
              {safeAuthors.length > 0 && (
                <span className="meta-item">
                  <User size={14} />
                  {safeAuthors.map((a: { name: string }) => a.name).filter(Boolean).join(', ')}
                </span>
              )}
              {resource.publishedDate && (
                <span className="meta-item">
                  <Calendar size={14} />
                  {formatDate(resource.publishedDate)}
                </span>
              )}
            </div>
            <div className="meta-right">
              {resource.downloadCount !== undefined && resource.downloadCount !== null && (
                <span className="meta-item">
                  <Download size={14} />
                  {resource.downloadCount}
                </span>
              )}
              {resource.views !== undefined && resource.views !== null && (
                <span className="meta-item">
                  <Eye size={14} />
                  {resource.views}
                </span>
              )}
            </div>
          </div>

          {safeTags.length > 0 && (
            <div className="resource-card-tags">
              {safeTags.slice(0, 3).map((tag: string, i: number) => (
                <span key={i} className="tag">{tag}</span>
              ))}
              {safeTags.length > 3 && (
                <span className="tag-more">+{safeTags.length - 3}</span>
              )}
            </div>
          )}

          <div className="resource-card-footer">
            <div className="footer-left">
              <span className="resource-id">
                ID: {resource._id!.slice(0, 8)}...
              </span>
              {resource.updatedAt && (
                <span className="resource-updated">
                  Updated {formatDate(resource.updatedAt)}
                </span>
              )}
            </div>
            <div className="footer-right">
              <div className="resource-actions">
                <button
                  type="button"
                  className="action-btn edit-btn"
                  onClick={() => onEdit(resource)}
                  title="Edit"
                  aria-label={`Edit ${resource.title || 'resource'}`}
                >
                  <Pencil size={14} />
                  <span>Edit</span>
                </button>
                <div className="action-menu-wrapper">
                  <button
                    type="button"
                    className="action-btn menu-btn"
                    onClick={(e) => onToggleActionMenu(resource._id!, e)}
                    aria-label="More actions"
                    aria-expanded={isActionMenuOpen}
                  >
                    <MoreVertical size={14} />
                  </button>
                  {isActionMenuOpen && (
                    <div className="action-menu-dropdown">
                      <button
                        type="button"
                        className="action-menu-item"
                        onClick={() => onEdit(resource)}
                      >
                        <Pencil size={14} />
                        Edit
                      </button>
                      <div className="action-menu-item">
                        <Pencil size={14} />
                        <span>Change Status</span>
                        <select
                          value={resource.status}
                          onChange={(event) => onStatusChange(resource._id!, event.target.value)}
                          aria-label={`Change status for ${resource.title || 'resource'}`}
                        >
                          {statusOptions.map((statusOption) => (
                            <option key={statusOption} value={statusOption}>
                              {formatStatusLabel(statusOption)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        className="action-menu-item action-menu-danger"
                        onClick={() => onDelete(resource._id!)}
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};